import {
  ArdoqId,
  DirectedTripleWithFilters,
  ViewpointBuilderFilters,
  QueryBuilderQuery,
  APIViewpointAttributes,
  CreateViewpointResponse,
  CreateViewpointAttributes,
  TraversalPathMatchingType,
  PathCollapsingRule,
  AssetType,
  APILoadTraversalResponse,
  StartSetExpansion,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  Method,
  crudApi,
  fetchJson,
  fetchVoidAndRespondWithId,
  renameEntity,
  toRequestOptions,
} from './api';
import {
  APICountInstancesResponse,
  ApiCountInstancesRequest,
} from '@ardoq/api-types';
import { omit } from 'lodash';
import { ardoqId, isArdoqError } from '@ardoq/common-helpers';
import { workspaceFolderApi } from './workspaceFolderApi';

export type APILoadTraversalParams = {
  startSet: ArdoqId[];
  paths: DirectedTripleWithFilters[][];
  startQuery: QueryBuilderQuery | null;
  filters: ViewpointBuilderFilters;
  pathMatching: TraversalPathMatchingType;
  pathCollapsingRules: PathCollapsingRule[]; // TODO needs BE-support
  includeAttachments?: boolean;
  startSetExpansion?: StartSetExpansion;
};

const loadTraversal = (
  loadTraversalRequest: APILoadTraversalParams,
  abortSignal?: AbortSignal
): ApiResponse<APILoadTraversalResponse> => {
  const request = new Request(
    '/api/traversal/execute',
    toRequestOptions(Method.POST, loadTraversalRequest, abortSignal)
  );
  return fetchJson<APILoadTraversalResponse>(request);
};

type Prompt = string;

export type APIInferTraversalRequest = {
  input: Prompt;
  startSet: ArdoqId[];
  clientRequestId: string;
};

const infer = (inferRequest: APIInferTraversalRequest): ApiResponse<string> => {
  const { clientRequestId, ...body } = inferRequest;

  const request = new Request(
    `/api/traversal/infer?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request, clientRequestId);
};

export type APISuggestTraversalRequest = {
  startSet: ArdoqId[];
  clientRequestId: string;
  referenceIds: ArdoqId[];
  parentChildReferenceIds: ArdoqId[];
};

const suggest = (
  suggestRequest: APISuggestTraversalRequest
): ApiResponse<string> => {
  const { clientRequestId, ...body } = suggestRequest;

  const request = new Request(
    `/api/traversal/suggest?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request, clientRequestId);
};

const countInstances = ({
  startSet,
  startQuery,
  paths,
  filters,
  startSetFilterId,
  pathMatching,
  startSetExpansion,
}: ApiCountInstancesRequest): ApiResponse<APICountInstancesResponse> => {
  const request = new Request(
    '/api/traversal/counts',
    toRequestOptions(Method.POST, {
      startSet: startQuery ? null : startSet,
      startQuery,
      paths,
      filters,
      startSetFilterId,
      pathMatching,
      startSetExpansion,
    })
  );
  return fetchJson<APICountInstancesResponse>(request);
};

const copy = (traversal: APIViewpointAttributes, newName: string) => {
  return traversalApi.create({
    name: newName,
    ...omit(traversal, [
      'name',
      '_id',
      'created',
      'createdBy',
      'createdByEmail',
      'createdByName',
      'lastModifiedBy',
      'lastModifiedByEmail',
      'lastModifiedByName',
      'lastUpdated',
      '_version',
    ]),
  });
};

const createTraversal = (
  traversal: CreateViewpointAttributes
): ApiResponse<CreateViewpointResponse> => {
  return fetchJson<CreateViewpointResponse>(
    new Request(
      '/api/traversal/create',
      toRequestOptions(Method.POST, traversal)
    )
  );
};

const createInFolder = async (
  viewpoint: CreateViewpointAttributes,
  folderId: ArdoqId
): ApiResponse<CreateViewpointResponse> => {
  const viewpointResponse = await traversalApi.createTraversal(viewpoint);
  if (isArdoqError(viewpointResponse)) {
    return viewpointResponse;
  }
  const moveAssetsResponse = await workspaceFolderApi.moveAssets({
    targetFolderId: folderId,
    content: [
      { _id: viewpointResponse.traversal._id, type: AssetType.TRAVERSAL },
    ],
  });
  if (isArdoqError(moveAssetsResponse)) {
    return moveAssetsResponse;
  }
  return viewpointResponse;
};

const ensureRuleTypeId = (
  viewpoint: APIViewpointAttributes
): APIViewpointAttributes => ({
  ...viewpoint,
  pathCollapsingRules: (viewpoint.pathCollapsingRules ?? []).map(rule => ({
    ...rule,
    referenceTypeId: rule.referenceTypeId || ardoqId(),
  })),
});

// Temporary fix to make the code work before we have the according migration
// done on the BE.
const crudApiWithRuleIdCheck = () => {
  const { fetch, fetchAll, ...rest } =
    crudApi<APIViewpointAttributes>('traversal');
  return {
    ...rest,
    fetchAll: async (): ApiResponse<APIViewpointAttributes[]> => {
      const result = await fetchAll();
      if (isArdoqError(result)) {
        return result;
      }
      return result.map(ensureRuleTypeId);
    },
    fetch: async (id: ArdoqId): ApiResponse<APIViewpointAttributes> => {
      const result = await fetch(id);
      if (isArdoqError(result)) {
        return result;
      }
      return ensureRuleTypeId(result);
    },
  };
};

export const traversalApi = {
  ...crudApiWithRuleIdCheck(),
  loadTraversal,
  infer,
  suggest,
  countInstances,
  copy,
  createTraversal,
  createInFolder,
  rename: renameEntity<APIViewpointAttributes>('traversal'),
};
